import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Languages, LogOut, Moon, Settings as SettingsIcon, Sun, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BranchSwitcher } from "@/components/BranchSwitcher";
import { SUPPORTED_LANGS, useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate } from "react-router-dom";

export function Topbar({ onToggleSidebar }) {
    const { user, tenant, logout } = useAuth();
    const { theme, setTheme } = useTheme();
    const { lang, setLang, t } = useT();
    const navigate = useNavigate();

    return (
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-zinc-200 bg-white/80 px-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/80">
            <div className="flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={onToggleSidebar}
                    data-testid="sidebar-toggle"
                    aria-label="Toggle navigation"
                >
                    <span className="grid place-items-center">
                        <span className="block h-0.5 w-4 bg-zinc-700 dark:bg-zinc-300" />
                        <span className="mt-1 block h-0.5 w-4 bg-zinc-700 dark:bg-zinc-300" />
                        <span className="mt-1 block h-0.5 w-4 bg-zinc-700 dark:bg-zinc-300" />
                    </span>
                </Button>
                <div className="hidden flex-col leading-tight md:flex">
                    <span className="font-display text-sm font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
                        {tenant?.name || t("brand.full")}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        {tenant?.slug || t("brand.name")}
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <BranchSwitcher />

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                    aria-label={t("common.theme")}
                    data-testid="theme-toggle"
                >
                    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label={t("common.lang")} data-testid="lang-menu">
                            <Languages className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-500">
                            {t("common.lang")}
                        </DropdownMenuLabel>
                        {SUPPORTED_LANGS.map((l) => (
                            <DropdownMenuItem
                                key={l.code}
                                onSelect={() => setLang(l.code)}
                                data-testid={`lang-${l.code}`}
                                className={lang === l.code ? "font-semibold text-brand-redDeep" : ""}
                            >
                                {l.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid="user-menu" className="gap-2">
                            <UserIcon className="h-4 w-4" />
                            <span className="hidden text-sm md:inline">{user?.name || user?.email}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="flex flex-col">
                            <span className="text-sm font-medium">{user?.name}</span>
                            <span className="text-xs text-zinc-500">{user?.email}</span>
                            <span className="mt-1 inline-flex w-fit rounded border border-brand-redSoft bg-brand-redSoft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-redDeep dark:border-brand-red/40 dark:bg-brand-red/15 dark:text-brand-red">
                                {user?.role}
                            </span>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => navigate("/settings")} data-testid="menu-settings">
                            <SettingsIcon className="mr-2 h-4 w-4" />
                            {t("nav.settings")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={logout} data-testid="menu-signout" className="text-rose-600">
                            <LogOut className="mr-2 h-4 w-4" />
                            {t("common.signout")}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
